import {
  ActionIcon,
  Box,
  Button,
  Center,
  Checkbox,
  Group,
  Loader,
  Select,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
  ButtonProps,
} from "@mantine/core"
import { DatePicker, MonthPicker, YearPicker } from "@mantine/dates"
import { Page } from "../../../../../-components/Page"
import { useNavigate } from "@tanstack/react-router"
import { Calendar } from "../../../-components/calendar"
import { match } from "ts-pattern"
import {
  CalendarWeekList,
  CalendarWeekListHandle,
} from "@/routes/app/-components/calendar/CalendarWeekList"
import { CalendarScheduler } from "@/routes/app/-components/calendar/scheduler"
import { useSearch } from "@tanstack/react-router"
import dayjs from "dayjs"
import {
  IconChevronLeft,
  IconChevronRight,
  IconSettings,
} from "@tabler/icons-react"
import { useDisclosure } from "@mantine/hooks"
import { DefaultModal } from "@components/default-modal"
import { useUserLocalStorage } from "@/routes/app/-components/use-user-local-storage"
import {
  eAppointmentCountingMethod,
  eAppointmentCountingMethod_AllValues,
} from "../../../../../@client/Theasoft_ApiServer_Calendar"
import { useTranslation } from "react-i18next"
import {
  getFormattedDate,
  getFormattedDatePeriod,
} from "@/utils/get-formatted-date"
import { useGetSimpleSeasonsByYearRange } from "@/orval/gen/api/season/season"
import { SimpleSeason } from "@/orval/gen/model/simpleSeason"
import { useIsSmallScreen } from "@/utils/useIsSmallScreen"

export type CalendarView =
  | "thea-year-grid"
  | "thea-year-table"
  | "thea-week-list"
  | "schedule-day"
  | "schedule-week"
  | "schedule-month"

const calendarViewOptions = [
  { value: "thea-year-grid", label: "Übersicht" },
  { value: "thea-week-list", label: "Liste" },
  { value: "thea-year-table", label: "Jahr" },
  { value: "schedule-month", label: "Monat" },
  { value: "schedule-week", label: "Woche" },
  { value: "schedule-day", label: "Tag" },
]

interface CalendarViewSettings {
  "thea-year-grid"?: {
    countingMethod?: eAppointmentCountingMethod
  }
  "thea-year-table"?: {
    countingMethod?: eAppointmentCountingMethod
    showEndTime?: boolean
  }
  schedule?: {
    showEndTime?: boolean
    showHolidays?: boolean
    showSchoolHolidays?: boolean
  }
}

function getDefaultCalendarViewSettings(): CalendarViewSettings {
  return {
    "thea-year-grid": {
      countingMethod: eAppointmentCountingMethod_AllValues[1],
    },
    "thea-year-table": {
      countingMethod: eAppointmentCountingMethod_AllValues[1],
      showEndTime: false,
    },
    schedule: {
      showEndTime: true,
      showHolidays: true,
      showSchoolHolidays: true,
    },
  }
}

type YearPickerMode = "calendar-year" | "season"

function isFullCalendarYear(start: dayjs.Dayjs, end: dayjs.Dayjs) {
  return (
    start.isSame(start.startOf("year"), "day") &&
    end.isSame(start.endOf("year"), "day")
  )
}

function getMatchingSeasonName(
  seasons: SimpleSeason[] | undefined,
  start: dayjs.Dayjs,
  end: dayjs.Dayjs
) {
  return getMatchingSeason(seasons, start, end)?.name
}

function getMatchingSeason(
  seasons: SimpleSeason[] | undefined,
  start: dayjs.Dayjs,
  end: dayjs.Dayjs
) {
  return seasons?.find(
    (season) =>
      dayjs(season.startDate).isSame(start, "day") &&
      dayjs(season.endDate).isSame(end, "day")
  )
}

function getCurrentSeason(
  seasons: SimpleSeason[] | undefined,
  date: dayjs.Dayjs
) {
  return seasons?.find(
    (season) =>
      !date.isBefore(dayjs(season.startDate), "day") &&
      !date.isAfter(dayjs(season.endDate), "day")
  )
}

function getYearPickerRange(date: Date) {
  const year = dayjs(date).year()
  const start = Math.floor(year / 10) * 10

  return { start, end: start + 9 }
}

const selectedSeasonButtonProps: ButtonProps = {
  variant: "filled",
  color: "var(--mantine-color-primary)",
  c: "var(--mantine-color-white)",
}

function SeasonPicker({
  date,
  value,
  onDateChange,
  onChange,
}: {
  date: Date
  value: SimpleSeason | undefined
  onDateChange: (date: Date) => void
  onChange: (season: SimpleSeason) => void
}) {
  const { t } = useTranslation("app_server")
  const seasonYearRange = getYearPickerRange(date)
  const { data: seasons, isLoading } = useGetSimpleSeasonsByYearRange({
    start: seasonYearRange.start,
    end: seasonYearRange.end,
  })

  const setYearRange = (year: number) => {
    onDateChange(dayjs(date).year(year).toDate())
  }

  return (
    <Stack gap="xs">
      <Group h={36} justify="space-between" wrap="nowrap">
        <ActionIcon
          aria-label={t("Back")}
          variant="subtle"
          color={"var(--mantine-color-text)"}
          onClick={() => setYearRange(seasonYearRange.start - 10)}
        >
          <IconChevronLeft size={16} />
        </ActionIcon>
        <Text
          fw={500}
          fz="sm"
        >{`${seasonYearRange.start} - ${seasonYearRange.end}`}</Text>
        <ActionIcon
          aria-label={t("Next")}
          variant="subtle"
          color={"var(--mantine-color-text)"}
          onClick={() => setYearRange(seasonYearRange.start + 10)}
        >
          <IconChevronRight size={16} />
        </ActionIcon>
      </Group>
      {isLoading ? (
        <Center mih={130}>
          <Loader size="sm" />
        </Center>
      ) : (
        <SimpleGrid cols={3} spacing={0}>
          {seasons?.map((season) => {
            const isSelected = value?.seasonId === season.seasonId

            return (
              <Button
                key={season.seasonId}
                variant="subtle"
                h={36}
                w={85}
                c="var(--mantine-color-text)"
                fw={400}
                {...(isSelected ? selectedSeasonButtonProps : {})}
                onClick={() => onChange(season)}
              >
                {season.name}
              </Button>
            )
          })}
        </SimpleGrid>
      )}
    </Stack>
  )
}

type YearPeriodPickerValue =
  | { type: "calendar-year"; date: Date }
  | { type: "season"; season: SimpleSeason }

function YearPeriodPicker({
  value,
  mode,
  onModeChange,
  onYearChange,
  onSeasonChange,
}: {
  value: YearPeriodPickerValue
  mode: YearPickerMode
  onModeChange: (mode: YearPickerMode) => void
  onYearChange: (date: string) => void
  onSeasonChange: (season: SimpleSeason) => void
}) {
  const { t } = useTranslation("app_server")
  const selectedPickerDate =
    value.type === "calendar-year"
      ? value.date
      : new Date(value.season.startDate)
  const selectedPickerDateTime = selectedPickerDate.getTime()
  const [pickerDate, setPickerDate] = React.useState(selectedPickerDate)

  React.useEffect(() => {
    setPickerDate(selectedPickerDate)
  }, [selectedPickerDateTime])

  return (
    <Stack gap="sm">
      <SegmentedControl
        value={mode}
        onChange={(newMode) => onModeChange(newMode as YearPickerMode)}
        data={[
          { value: "calendar-year", label: t("CalendarYears") },
          { value: "season", label: t("Seasons") },
        ]}
      />
      {mode === "calendar-year" ? (
        <YearPicker
          value={value.type === "calendar-year" ? value.date : null}
          date={pickerDate}
          onDateChange={(date) => setPickerDate(new Date(date))}
          onChange={(date) => date && onYearChange(date)}
        />
      ) : (
        <SeasonPicker
          date={pickerDate}
          value={value.type === "season" ? value.season : undefined}
          onDateChange={setPickerDate}
          onChange={onSeasonChange}
        />
      )}
    </Stack>
  )
}

export function CalendarPage() {
  const {
    startDate,
    endDate,
    view = "thea-year-table",
  } = useSearch({
    from: "/app/my/calendar/",
  })
  const navigate = useNavigate({ from: "/app/my/calendar/" })
  const { t } = useTranslation("app_server")
  const [pickerOpened, pickerModal] = useDisclosure(false)
  const [settingsOpened, settingsModal] = useDisclosure(false)
  const weekListRef = React.useRef<CalendarWeekListHandle>(null)
  const [yearPickerMode, setYearPickerMode] =
    useUserLocalStorage<YearPickerMode>({
      key: "calendar-year-picker-mode",
      defaultValue: "calendar-year",
    })
  const [viewSettings, setViewSettings] =
    useUserLocalStorage<CalendarViewSettings>({
      key: "calendar-view-display-settings",
      defaultValue: getDefaultCalendarViewSettings(),
    })
  const searchStartDate =
    startDate && dayjs(startDate).isValid() ? dayjs(startDate) : undefined
  const searchEndDate =
    endDate && dayjs(endDate).isValid() ? dayjs(endDate) : undefined

  const updateSearchStartDate = (date: Date) => {
    navigate({
      search: {
        view,
        startDate: dayjs(date).format("YYYY-MM-DD"),
        endDate: undefined,
      },
    })
  }

  const updateSearchDatePeriod = (start: Date, end: Date) => {
    navigate({
      search: {
        view,
        startDate: dayjs(start).format("YYYY-MM-DD"),
        endDate: dayjs(end).format("YYYY-MM-DD"),
      },
    })
  }

  const handleDateChange = (date: Date) => {
    updateSearchStartDate(date)
  }

  const handleViewChange = (newView: CalendarView) => {
    navigate({
      search: { view: newView, startDate: undefined, endDate: undefined },
    })
  }
  const handleScheduleViewChange = (
    newView: "day" | "week" | "month" | "year",
    date?: Date
  ) => {
    const targetView =
      newView === "year"
        ? "thea-year-grid"
        : (`schedule-${newView}` as CalendarView)
    const targetDate = date ?? new Date()

    if (newView === "year") {
      const yearStart = dayjs(targetDate).startOf("year")
      navigate({
        search: {
          view: "thea-year-grid",
          startDate: yearStart.format("YYYY-MM-DD"),
          endDate: yearStart.endOf("year").format("YYYY-MM-DD"),
        },
      })
    } else {
      navigate({
        search: {
          view: targetView,
          startDate: dayjs(targetDate).format("YYYY-MM-DD"),
          endDate: undefined,
        },
      })
    }
  }

  const yearGridSettings = viewSettings["thea-year-grid"]
  const yearSettings = viewSettings["thea-year-table"]
  const scheduleSettings = viewSettings["schedule"]
  const selectedYearPeriodDate = searchStartDate ?? dayjs()
  const selectedSeasonYearRange = getYearPickerRange(
    selectedYearPeriodDate.toDate()
  )
  const { data: seasonsForSelectedPeriod } = useGetSimpleSeasonsByYearRange({
    start: selectedSeasonYearRange.start - 1,
    end: selectedSeasonYearRange.end + 1,
  })
  const defaultSeason =
    !searchStartDate && !searchEndDate && yearPickerMode === "season"
      ? getCurrentSeason(seasonsForSelectedPeriod, dayjs())
      : undefined
  const defaultYearStartDate = dayjs().startOf("year")
  const defaultYearEndDate = dayjs().endOf("year")
  const yearGridStartDate = searchStartDate
    ? searchStartDate
    : defaultSeason
      ? dayjs(defaultSeason.startDate)
      : defaultYearStartDate
  const yearTableStartDate = searchStartDate
    ? searchStartDate
    : defaultSeason
      ? dayjs(defaultSeason.startDate)
      : defaultYearStartDate
  const yearGridEndDate = searchEndDate
    ? searchEndDate
    : defaultSeason
      ? dayjs(defaultSeason.endDate)
      : defaultYearEndDate
  const yearTableEndDate = searchEndDate
    ? searchEndDate
    : defaultSeason
      ? dayjs(defaultSeason.endDate)
      : defaultYearEndDate
  const getYearPeriodPickerValue = (
    start: dayjs.Dayjs,
    end: dayjs.Dayjs
  ): YearPeriodPickerValue => {
    const selectedSeason = getMatchingSeason(
      seasonsForSelectedPeriod,
      start,
      end
    )

    return selectedSeason
      ? { type: "season", season: selectedSeason }
      : { type: "calendar-year", date: start.toDate() }
  }
  const weekListStartDate = searchStartDate ?? dayjs().startOf("isoWeek")
  const scheduleDayStartDate = searchStartDate ?? dayjs()
  const scheduleWeekStartDate = searchStartDate ?? dayjs().startOf("isoWeek")
  const scheduleMonthStartDate = searchStartDate ?? dayjs().startOf("month")

  const updateYearPeriodBy = (
    start: dayjs.Dayjs,
    end: dayjs.Dayjs,
    amount: -1 | 1
  ) => {
    const selectedSeason = getMatchingSeason(
      seasonsForSelectedPeriod,
      start,
      end
    )

    if (selectedSeason) {
      const seasons = [...(seasonsForSelectedPeriod ?? [])].sort(
        (first, second) =>
          dayjs(first.startDate).valueOf() - dayjs(second.startDate).valueOf()
      )
      const selectedSeasonIndex = seasons.findIndex(
        (season) => season.seasonId === selectedSeason.seasonId
      )
      const adjacentSeason = seasons[selectedSeasonIndex + amount]

      if (adjacentSeason) {
        updateSearchDatePeriod(
          new Date(adjacentSeason.startDate),
          new Date(adjacentSeason.endDate)
        )
        return
      }
    }

    const nextStart = start.add(amount, "year").startOf("year")
    updateSearchDatePeriod(nextStart.toDate(), nextStart.endOf("year").toDate())
  }

  const updatePeriodBy = (amount: -1 | 1) => {
    match(view)
      .with("thea-year-grid", () =>
        updateYearPeriodBy(yearGridStartDate, yearGridEndDate, amount)
      )
      .with("thea-year-table", () =>
        updateYearPeriodBy(yearTableStartDate, yearTableEndDate, amount)
      )
      .with("thea-week-list", () =>
        updateSearchStartDate(
          weekListStartDate.add(amount, "week").startOf("isoWeek").toDate()
        )
      )
      .with("schedule-day", () =>
        updateSearchStartDate(scheduleDayStartDate.add(amount, "day").toDate())
      )
      .with("schedule-week", () =>
        updateSearchStartDate(
          scheduleWeekStartDate.add(amount, "week").startOf("isoWeek").toDate()
        )
      )
      .with("schedule-month", () =>
        updateSearchStartDate(
          scheduleMonthStartDate.add(amount, "month").startOf("month").toDate()
        )
      )
      .exhaustive()
  }

  const handleToday = () => {
    if (view === "thea-week-list") {
      if (weekListRef.current) {
        weekListRef.current.scrollToToday()
      } else {
        updateSearchStartDate(dayjs().startOf("isoWeek").toDate())
      }
      return
    }

    navigate({
      search: { view, startDate: undefined, endDate: undefined },
    })
  }

  const availableCountingMethods = [
    {
      value: eAppointmentCountingMethod_AllValues[1],
      label: t("CountAll"),
    },
    {
      value: eAppointmentCountingMethod_AllValues[2],
      label: t("CountPerformances"),
    },
  ]

  const InnerComponent = match(view)
    .with("thea-year-grid", () => (
      <Calendar
        view="grid"
        startDate={yearGridStartDate.toDate()}
        endDate={yearGridEndDate.toDate()}
        countingMethod={yearGridSettings?.countingMethod}
      />
    ))
    .with("thea-year-table", () => (
      <Calendar
        view="table"
        startDate={yearTableStartDate.toDate()}
        endDate={yearTableEndDate.toDate()}
        countingMethod={yearSettings?.countingMethod}
        showEndTime={yearSettings?.showEndTime}
      />
    ))
    .with("thea-week-list", () => (
      <CalendarWeekList
        ref={weekListRef}
        startDate={weekListStartDate.startOf("isoWeek").toDate()}
        onStartDateChange={handleDateChange}
      />
    ))
    .with("schedule-day", () => (
      <CalendarScheduler
        startDate={scheduleDayStartDate.toDate()}
        view="day"
        setView={handleScheduleViewChange}
        onDateChange={handleDateChange}
        showEndTime={scheduleSettings?.showEndTime}
        showHolidays={scheduleSettings?.showHolidays}
        showSchoolHolidays={scheduleSettings?.showSchoolHolidays}
      />
    ))
    .with("schedule-week", () => (
      <CalendarScheduler
        startDate={scheduleWeekStartDate.startOf("isoWeek").toDate()}
        view="week"
        setView={handleScheduleViewChange}
        onDateChange={handleDateChange}
        showEndTime={scheduleSettings?.showEndTime}
        showHolidays={scheduleSettings?.showHolidays}
        showSchoolHolidays={scheduleSettings?.showSchoolHolidays}
      />
    ))
    .with("schedule-month", () => (
      <CalendarScheduler
        startDate={scheduleMonthStartDate.startOf("month").toDate()}
        view="month"
        setView={handleScheduleViewChange}
        onDateChange={handleDateChange}
        showEndTime={scheduleSettings?.showEndTime}
        showHolidays={scheduleSettings?.showHolidays}
        showSchoolHolidays={scheduleSettings?.showSchoolHolidays}
      />
    ))
    .exhaustive()

  const [hoveredDate, setHoveredDate] = React.useState<Date | null>(null)

  const pickerContent = match(view)
    .with("thea-year-grid", () => (
      <YearPeriodPicker
        mode={yearPickerMode}
        onModeChange={setYearPickerMode}
        value={getYearPeriodPickerValue(yearGridStartDate, yearGridEndDate)}
        onYearChange={(date) => {
          const yearStart = dayjs(date).startOf("year")
          updateSearchDatePeriod(
            yearStart.toDate(),
            yearStart.endOf("year").toDate()
          )
          pickerModal.close()
        }}
        onSeasonChange={(season) => {
          updateSearchDatePeriod(
            new Date(season.startDate),
            new Date(season.endDate)
          )
          pickerModal.close()
        }}
      />
    ))
    .with("thea-year-table", () => (
      <YearPeriodPicker
        mode={yearPickerMode}
        onModeChange={setYearPickerMode}
        value={getYearPeriodPickerValue(yearTableStartDate, yearTableEndDate)}
        onYearChange={(date) => {
          const yearStart = dayjs(date).startOf("year")
          updateSearchDatePeriod(
            yearStart.toDate(),
            yearStart.endOf("year").toDate()
          )
          pickerModal.close()
        }}
        onSeasonChange={(season) => {
          updateSearchDatePeriod(
            new Date(season.startDate),
            new Date(season.endDate)
          )
          pickerModal.close()
        }}
      />
    ))
    .with("thea-week-list", () => (
      <DatePicker
        value={weekListStartDate.toDate()}
        onChange={(date) => {
          if (!date) return
          handleDateChange(dayjs(date).startOf("isoWeek").toDate())
          pickerModal.close()
        }}
      />
    ))
    .with("schedule-day", () => (
      <DatePicker
        value={scheduleDayStartDate.toDate()}
        onChange={(date) => {
          if (!date) return
          handleDateChange(dayjs(date).toDate())
          pickerModal.close()
        }}
      />
    ))
    .with("schedule-week", () => {
      const startOfWeek = scheduleWeekStartDate.startOf("isoWeek").toDate()
      const endOfWeek = scheduleWeekStartDate.endOf("isoWeek").toDate()
      return (
        <DatePicker
          type="range"
          value={[startOfWeek, endOfWeek]}
          onChange={(dates) => {
            if (!dates) return
            handleDateChange(dayjs(dates[0]).startOf("isoWeek").toDate())
            pickerModal.close()
          }}
          withWeekNumbers
          onMouseLeave={() => setHoveredDate(null)}

          getDayProps={(date) => {
            const startOfHovered = hoveredDate
              ? dayjs(hoveredDate).startOf("week")
              : null
            const endOfHovered = hoveredDate
              ? dayjs(hoveredDate).endOf("week")
              : null
            const current = dayjs(date)

            const isInHoveredWeek =
              startOfHovered && endOfHovered
                ? current.isAfter(startOfHovered.subtract(1, "day")) &&
                  current.isBefore(endOfHovered)
                : false

            return {
              onMouseEnter: () => setHoveredDate(new Date(date)),

              style: isInHoveredWeek
                ? {
                    backgroundColor: "var(--mantine-color-blue-light)",
                    borderRadius: 0,
                  }
                : {},
            }
          }}
        />
      )
    })
    .with("schedule-month", () => (
      <MonthPicker
        value={scheduleMonthStartDate.toDate()}
        onChange={(date) => {
          if (!date) return
          handleDateChange(dayjs(date).startOf("month").toDate())
          pickerModal.close()
        }}
      />
    ))
    .exhaustive()

  const settingsContent = match(view)
    .with("thea-year-grid", () => (
      <Select
        label={t("AppointmentCountMode")}
        value={yearGridSettings?.countingMethod}
        onChange={(countingMethod) => {
          if (!countingMethod) return
          setViewSettings((current) => ({
            ...current,
            "thea-year-grid": {
              ...current["thea-year-grid"],
              countingMethod: countingMethod as eAppointmentCountingMethod,
            },
          }))
        }}
        data={availableCountingMethods}
      />
    ))
    .with("thea-year-table", () => (
      <Stack gap="sm">
        <Select
          label={t("AppointmentCountMode")}
          value={yearSettings?.countingMethod}
          onChange={(countingMethod) => {
            if (!countingMethod) return
            setViewSettings((current) => ({
              ...current,
              "thea-year-table": {
                ...current["thea-year-table"],
                countingMethod: countingMethod as eAppointmentCountingMethod,
              },
            }))
          }}
          data={availableCountingMethods}
        />
        <Checkbox
          label={t("ShowEndTime")}
          checked={yearSettings?.showEndTime}
          onChange={(event) =>
            setViewSettings((current) => ({
              ...current,
              "thea-year-table": {
                ...current["thea-year-table"],
                showEndTime: event.target.checked,
              },
            }))
          }
        />
      </Stack>
    ))
    .with("schedule-month", "schedule-week", "schedule-day", () => (
      <Stack gap="sm">
        <Checkbox
          label={t("ShowEndTime")}
          checked={scheduleSettings?.showEndTime}
          onChange={(event) =>
            setViewSettings((current) => ({
              ...current,
              schedule: {
                ...current.schedule,
                showEndTime: event.target.checked,
              },
            }))
          }
        />
        <Checkbox
          label={t("ShowHolidays")}
          checked={scheduleSettings?.showHolidays}
          onChange={(event) =>
            setViewSettings((current) => ({
              ...current,
              schedule: {
                ...current.schedule,
                showHolidays: event.target.checked,
              },
            }))
          }
        />
        <Checkbox
          label={t("ShowSchoolHolidays")}
          checked={scheduleSettings?.showSchoolHolidays}
          onChange={(event) =>
            setViewSettings((current) => ({
              ...current,
              schedule: {
                ...current.schedule,
                showSchoolHolidays: event.target.checked,
              },
            }))
          }
        />
      </Stack>
    ))
    .otherwise(() => null)

  const selectedDateOrPeriod = match(view)
    .with("thea-year-grid", () => {
      const seasonName = getMatchingSeasonName(
        seasonsForSelectedPeriod,
        yearGridStartDate,
        yearGridEndDate
      )

      return seasonName
        ? seasonName
        : isFullCalendarYear(yearGridStartDate, yearGridEndDate)
          ? yearGridStartDate.format("YYYY")
          : getFormattedDatePeriod({
              start: yearGridStartDate.format("YYYY-MM-DD"),
              end: yearGridEndDate.format("YYYY-MM-DD"),
            })
    })
    .with("thea-year-table", () => {
      const seasonName = getMatchingSeasonName(
        seasonsForSelectedPeriod,
        yearTableStartDate,
        yearTableEndDate
      )

      return seasonName
        ? seasonName
        : isFullCalendarYear(yearTableStartDate, yearTableEndDate)
          ? yearTableStartDate.format("YYYY")
          : getFormattedDatePeriod({
              start: yearTableStartDate.format("YYYY-MM-DD"),
              end: yearTableEndDate.format("YYYY-MM-DD"),
            })
    })
    .with("thea-week-list", () => getFormattedDate(weekListStartDate.toDate()))
    .with("schedule-day", () => getFormattedDate(scheduleDayStartDate.toDate()))
    .with("schedule-week", () =>
      getFormattedDatePeriod({
        start: scheduleWeekStartDate.startOf("isoWeek").format("YYYY-MM-DD"),
        end: scheduleWeekStartDate.endOf("isoWeek").format("YYYY-MM-DD"),
      })
    )
    .with("schedule-month", () => scheduleMonthStartDate.format("MMMM YYYY"))
    .exhaustive()
  const isSmallScreen = useIsSmallScreen()
  const todayButton = (
    <Button
      size="xs"
      onClick={handleToday}
      variant="default"
      {...(isSmallScreen && { bdrs: 25 })}
    >
      {t("Today")}
    </Button>
  )

  const crumbOverride = React.useCallback(
    () => (
      <Group>
        {!isSmallScreen ? (
          <Text fw={500} fz="xl">
            {t("Calendar")}
          </Text>
        ) : null}
        <Group gap={4} wrap="nowrap">
          <Button
            size="xs"
            leftSection={
              <ActionIcon
                color="var(--mantine-color-text)"
                aria-label={t("Back")}
                variant="subtle"
                onClick={(e) => {
                  e.stopPropagation()
                  updatePeriodBy(-1)
                }}
              >
                <IconChevronLeft size={16} />
              </ActionIcon>
            }
            rightSection={
              <ActionIcon
                color="var(--mantine-color-text)"
                aria-label={t("Next")}
                variant="subtle"
                onClick={(e) => {
                  e.stopPropagation()
                  updatePeriodBy(1)
                }}
              >
                <IconChevronRight size={16} />
              </ActionIcon>
            }
            aria-label={t("Calendar")}
            variant="default"
            onClick={pickerModal.open}
            px={0}
            fz={14}
          >
            {selectedDateOrPeriod}
          </Button>

          {!isSmallScreen ? todayButton : null}
        </Group>
      </Group>
    ),
    [selectedDateOrPeriod, pickerModal, t, isSmallScreen, todayButton]
  )
  return (
    <>
      <Page
        scrollContainerProps={{ px: 0 }}
        crumbOverride={crumbOverride}
        buttonGroupProps={{
          children: (
            <Group gap={4} wrap="nowrap">
              {settingsContent ? (
                <Tooltip label={t("Settings")}>
                  <ActionIcon
                    aria-label={t("Settings")}
                    variant="default"
                    onClick={settingsModal.open}
                    h={30}
                    w={30}
                  >
                    <IconSettings size={16} stroke={1.75} />
                  </ActionIcon>
                </Tooltip>
              ) : null}
              <Select
                w={100}
                size="xs"
                styles={{ input: { fontSize: 14 } }}
                data={calendarViewOptions}
                value={view}
                onChange={(newView) =>
                  newView && handleViewChange(newView as CalendarView)
                }
              />
            </Group>
          ),
        }}
      >
        {InnerComponent}
      </Page>
      {isSmallScreen ? (
        <Box pos="fixed" bottom={20} right={20} style={{ zIndex: 200 }}>
          {todayButton}
        </Box>
      ) : null}
      <DefaultModal
        opened={pickerOpened}
        onClose={pickerModal.close}
        title={t("Calendar")}
        size="auto"
      >
        <Center>{pickerContent}</Center>
      </DefaultModal>
      <DefaultModal
        opened={settingsOpened}
        onClose={settingsModal.close}
        title={`${t("Calendar")} ${t("Settings")}`}
      >
        {settingsContent}
      </DefaultModal>
    </>
  )
}
